import { useEffect } from "react";
import { Link } from "react-router-dom";

interface ValueCard {
  icon: string;
  title: string;
  description: string;
}

const values: ValueCard[] = [
  {
    icon: "🔍",
    title: "Independence",
    description:
      "We have no commercial relationships with any dental clinic. Our recommendations are based purely on research, not partnerships or commissions.",
  },
  {
    icon: "🛡️",
    title: "Patient Safety First",
    description:
      'Every guide and checklist starts from one question: "What does the patient need to know to protect themselves?" Not what\'s easiest to say — what\'s most important.',
  },
  {
    icon: "📊",
    title: "Honest, Balanced Information",
    description:
      "We highlight both the opportunities and the risks of dental tourism. We won't pretend every clinic is safe or that every destination suits every patient.",
  },
  {
    icon: "💬",
    title: "Plain-Language Guidance",
    description:
      "No jargon. No sales language. No scare tactics. Just clear, practical information that helps you make a decision you'll feel confident about.",
  },
];

export default function About() {
  useEffect(() => {
    const originalTitle = document.title;
    document.title = "About Us – Dental Safety Abroad";
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Dental Safety Abroad is an independent information resource helping UK patients navigate dental treatment abroad safely, affordably and confidently."
      );
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute("content", originalDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-[#182940] pt-24 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 lg:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <nav
              className="flex items-center justify-center gap-2 text-xs text-white/50 mb-6"
              aria-label="Breadcrumb"
            >
              <Link to="/" className="hover:text-white/80 transition-colors">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-white/70" aria-current="page">
                About
              </span>
            </nav>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              About Dental Safety Abroad
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-2xl mx-auto">
              We're an independent information resource — not a clinic, not an
              agency. Just honest guidance for people considering dental
              treatment abroad.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
                Our Story
              </span>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-5">
                Why We Built This
              </h2>
              <div className="space-y-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  Dental costs in the UK have become increasingly unaffordable
                  for many people. NHS waiting lists are long, private prices
                  are steep, and the gap between what people need and what they
                  can access has grown year on year.
                </p>
                <p>
                  At the same time, dental tourism has exploded — with hundreds
                  of thousands of UK patients travelling to Hungary, Turkey,
                  Poland and beyond for treatment that would cost a fraction of
                  the price.
                </p>
                <p>
                  But the internet is full of conflicting, commercially
                  motivated information. Clinic websites promise everything.
                  Review platforms can be gamed. And patients — often making the
                  biggest dental investment of their lives — are left trying to
                  navigate this alone.
                </p>
                <p>
                  <strong className="text-foreground-900">
                    Dental Safety Abroad exists to change that.
                  </strong>{" "}
                  We provide clear, independent, research-backed guidance — with
                  no clinic partnerships, no referral fees, and no agenda beyond
                  helping you make a genuinely informed decision.
                </p>
              </div>
              <div className="mt-6">
                <Link
                  to="/checklist"
                  className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Get the 7 Red Flags That Could Ruin Your Dental Treatment Abroad
                </Link>
              </div>
            </div>
            <div>
              <div className="rounded-lg overflow-hidden border border-background-200/70">
                <img
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80"
                  alt="Professional team working at a desk in a bright, modern office"
                  width={450}
                  height={320}
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-background-100/70"
        id="values"
      >
        <div className="max-w-5xl mx-auto text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
            Our Values
          </span>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-3">
            What We Stand For
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            Every resource we publish is built around these four principles.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v, i) => (
              <div
                key={i}
                className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6 text-left"
              >
                <div className="text-2xl md:text-3xl mb-3">{v.icon}</div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  {v.title}
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {v.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Transparency */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14">
        <div className="max-w-5xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
            Transparency
          </span>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-6">
            What We Are — and What We're Not
          </h2>

          <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-7 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center text-green-600">
                    <i className="ri-check-line"></i>
                  </span>
                  We are:
                </h4>
                <ul className="space-y-2">
                  {[
                    "An independent information and education resource",
                    "A guide to help you ask the right questions",
                    "A publisher of research-backed destination comparisons",
                    "Transparent about our use of affiliate links (see disclosure)",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground-600"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 shrink-0" />
                      {item ===
                      "Transparent about our use of affiliate links (see disclosure)" ? (
                        <span>
                          Transparent about our use of affiliate links (see{" "}
                          <Link
                            to="/legal"
                            className="text-primary-600 hover:underline"
                          >
                            disclosure
                          </Link>
                          )
                        </span>
                      ) : (
                        item
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center text-red-500">
                    <i className="ri-close-line"></i>
                  </span>
                  We are not:
                </h4>
                <ul className="space-y-2">
                  {[
                    "A dental clinic or treatment provider",
                    "A medical adviser or diagnostician",
                    "A booking or referral agency",
                    "Affiliated with or paid by any specific clinic",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground-600"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-amber-50/70 border border-amber-200/60 rounded-lg p-4 md:p-5">
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong>⚠️ Disclaimer:</strong> Nothing on this website
              constitutes medical or dental advice. Always consult a qualified
              dental professional before making any treatment decisions. See our
              full{" "}
              <Link to="/legal" className="text-primary-600 hover:underline">
                disclaimer
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section
        className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 bg-accent-100/70"
        id="team"
      >
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
            The Founder
          </span>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-3">
            Who's Behind This?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            We're a small, independent team with a shared passion for helping UK
            patients make safer, smarter decisions about dental treatment abroad.
          </p>

          <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 text-center mb-6">
            <h4 className="text-base font-semibold text-foreground-900 mb-1">
              Amber Georgeson
            </h4>
            <p className="text-xs text-primary-600 font-medium mb-4">
              Founder & Editor
            </p>
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed text-left">
              <p>
                My investigation into dental tourism began with a clear objective:
                to create a resource that cuts through misinformation and puts
                patient safety first. Drawing on extensive experience in critical
                evaluation, structured questioning, and risk assessment, I have
                applied a rigorous, evidence-led approach to identifying where
                dental treatment abroad can go wrong.
              </p>
              <p>
                This research underpins our country guides and red flag guide.
                The focus is simple: to equip patients with the right questions,
                the right frameworks, and the clarity needed to make informed,
                defensible decisions before committing to treatment.
              </p>
            </div>
          </div>

          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            Get in Touch →
          </Link>
        </div>
      </section>
    </div>
  );
}